import { i18n } from 'boot/i18n';

/** @typedef {'password'} PasswordProviderKey */
/** @typedef {'microsoft.com'} MicrosoftProviderId */
/** @typedef {'apple.com'} AppleProviderId */
/** @typedef {'google.com'} GoogleProviderId */

/** @typedef { MicrosoftProviderId | AppleProviderId | GoogleProviderId } SsoProvider */
/** @typedef { PasswordProviderKey | SsoProvider } AuthMethod */

/**
 * @typedef {{ type: SsoProvider }} SsoData
 */

/**
 * @typedef {Object & SsoData} MicrosoftSsoData
 *
 * @property {string} applicationId - Azure Entra App's application ID
 * @property {string} tenantId - Azure Entra App's directory ID
 * @property {string} tenantName - Azure Entra's Tenant Name
 */

/**
 * All supported SSO providers must be listed in this object
 *
 * @type {{[key: string]: SsoProvider }}
 */
export const SSO_PROVIDER = {
  GOOGLE: 'google.com',
  APPLE: 'apple.com',
  MICROSOFT: 'microsoft.com',
};

export const SSO_PROVIDERS = Object.values(SSO_PROVIDER);

/**
 * @type {{[key: string]: AuthMethod}}
 */
export const LOGIN_METHOD = {
  PASSWORD: 'password',
  GOOGLE: SSO_PROVIDER.GOOGLE,
  APPLE: SSO_PROVIDER.APPLE,
  MICROSOFT: SSO_PROVIDER.MICROSOFT,
};

export const LOGIN_METHODS = Object.values(LOGIN_METHOD);

export const AUTH_ERROR = {
  FIRESTORE_PERMISSION: 'permission-denied',
  CLIENT_OFFLINE: 'Failed to get document because the client is offline.',
  INVITATION_NOT_FOUND: 'Invitation not found',
  INVITATION_WRONG_REGION: 'Invitation found in another region',
  SIGN_UP_METHOD: 'Wrong sign up method',
  INSUFFICIENT_PERMISSIONS: 'Insufficient permissions',
};

export const AUTH_ERROR_CODE = {
  EMAIL_ALREADY_IN_USE: 'auth/email-already-in-use',
  EXISTS_WITH_DIFFERENT_CREDENTIAL: 'auth/account-exists-with-different-credential',
  MULTI_FACTOR_REQUIRED: 'auth/multi-factor-auth-required',
  NETWORK_REQUEST_FAILED: 'auth/network-request-failed',
  OPERATION_NOT_ALLOWED: 'auth/operation-not-allowed',
  POPUP_CANCELLED_BY_USER: 'auth/cancelled-popup-request',
  POPUP_CLOSED_BY_USER: 'auth/popup-closed-by-user',
  USER_CANCELLED: 'auth/user-cancelled',
  USER_DISABLED: 'auth/user-disabled',
  USER_MISMATCH: 'auth/user-mismatch',
  USER_NOT_FOUND: 'auth/user-not-found',
  MISSING_PASSWORD: 'auth/missing-password',
  WRONG_PASSWORD: 'auth/wrong-password',
};

// eslint-disable-next-line max-len
export const MICROSOFT_AUTH_KNOWLEDGE_BASE_LINK = 'https://help.hirasmus.com/knowledge/single-sign-on-setting-up-microsoft-sso';

export const MFA_METHODS = {
  AUTHENTICATOR_APP: {
    ID: 'totp',
    LABEL: 'authenticator app',
  },
  PHONE_NUMBER: {
    ID: 'phone',
    LABEL: 'phone number',
  },
};

export const AUTH_SESSIONS_INDEX_DB = 'authSessions';

export const WARNING_DELAY_BEFORE_AUTO_LOGOUT = 20 * 1000; // 20 sec

export const AUTH_SESSIONS_STATUS = {
  ACTIVE: 'active', // Auto-logout is enabled, and the auth session timer is running
  EXPIRING: 'expiring', // The auth session is about to expire, so we show a warning banner
  EXPIRED: 'expired', // The auth session has expired, so the user is logged out without saving unsaved data
};

export const AUTH_SESSIONS_OPTIONS = [
  { label: `9 ${i18n.t('measures.hours')}`, value: 32400000 },
  { label: `24 ${i18n.t('measures.hours')}`, value: 86400000 },
  { label: `1 ${i18n.t('measures.week')}`, value: 604800000 },
];

export const AUTH_SESSION_WORKER_COMMANDS = {
  START: 'start',
  STOP: 'stop',
};

export const AUTH_SESSION_WORKER_EVENTS = {
  TICK: 'tick',
  STOPPED: 'stopped',
};
